import React, { useEffect, useMemo, useRef, useState} from "react";
import {Button} from "./Button.tsx";
import IconTrash from '../assets/icons/trash.svg?react';
import {AuthenticationRepository} from "../repositories/AuthenticationRepository.ts";
import {TripbookRepository} from "../repositories/TripbookRepository.ts";
import {useTripbooks} from "../hooks/useTripbooks.ts";
import {Tripbook} from "../domain/entities/Tripbook.ts";
import {TripBookMiniature} from "./TripBookMiniature.tsx";
import {Agency} from "../domain/entities/Agency.ts";
import {getAgencyAssetUrl} from "../utils/AssetsCDN.ts";
import {getFlagUrl} from "../utils/flagUrl.ts";
import threeDotsMenuIcon from "../assets/icons/three-dots-menu.png";
import downloadIcon from "../assets/icons/download.png";
import deleteIcon from "../assets/icons/delete.png";
import editIcon from "../assets/icons/edit.png";
import infoIcon from "../assets/icons/info.png";
import {ConfirmDialog} from "./dialogs/ConfirmDialog.tsx";
import {useTranslation} from "react-i18next";
import {CreateUpdateTripBookDialog} from "./dialogs/CreateUpdateTripBookDialog.tsx";
import {DeleteTripbookUseCase} from "../domain/useCases/tripbooks/DeleteTripbookUseCase.ts";
import {Dialog} from "./Dialog.tsx";
import {useSelector} from "react-redux";
import {AutocompleteSearch} from "./AutocompleteSearch.tsx";
import {debounceTime, Subject} from "rxjs";
import {WarmTripbookUseCase} from "../domain/useCases/tripbooks/WarmTripbookUseCase.ts";
import {Input} from "./Input.tsx";
import {ShareTripbookUseCase} from "../domain/useCases/tripbooks/ShareTripbookUseCase.ts";
import IconSpinner from "../assets/icons/spinner.svg?react";
import IconShare from "../assets/icons/share.svg";
import {AnalyticsEvents, trackAnalyticsEvent} from "../utils/ProductAnalytics.ts";
import {DownloadEbookUseCase} from "../domain/useCases/tripbooks/DownloadEbookUseCase.ts";
import {List, RowComponentProps} from "react-window";
import {createPortal} from "react-dom";
import {useAgencies} from "../hooks/useAgencies.ts";

const getFormattedDateFromTimestamp = (timestamp: number, includeYear: boolean) => {
    const date = new Date(timestamp);
    if(includeYear){
        //return for example 15 Jun 2024
        return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`
    } else {
        //return for example 15 Jun
        return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`
    }
}

const WIDE_LAYOUT_BREAKPOINT = 1150;

const useContainerWidth = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<number>(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            setWidth(entries[0].contentRect.width);
        });
        observer.observe(el);
        setWidth(el.getBoundingClientRect().width);
        return () => observer.disconnect();
    }, []);

    return { ref, width };
};

const deleteTripbookUseCase = new DeleteTripbookUseCase(new TripbookRepository(new AuthenticationRepository()));
const warmTripbookUseCase = new WarmTripbookUseCase(new TripbookRepository(new AuthenticationRepository()))
const shareTripbookUseCase = new ShareTripbookUseCase(new TripbookRepository(new AuthenticationRepository()));
const downloadEbookUseCase = new DownloadEbookUseCase(new TripbookRepository(new AuthenticationRepository()));

type TripbooksContainerProps = {
    displayCreateTripItineraryDialog: any
    setPath?: (path: string) => void
}


const MenuPortal = ({ children, top, left, onClose }) => {
    // Renderizamos en document.body para escapar del overflow:hidden de la lista
    return createPortal(
        <>
            {/* Backdrop transparente para cerrar al hacer click fuera */}
            <div
                className="fixed inset-0 z-[40]"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            />
            {/* El menú en sí */}
            <div
                className="fixed z-[50] bg-white rounded-2xl shadow-md flex flex-col w-[220px]"
                style={{
                    top: `${top}px`,
                    left: `${left}px`,
                    boxShadow: "0px 0px 4px 0px #00000040"
                }}
            >
                {children}
            </div>
        </>,
        document.body
    );
};


const TripbookRow = ({ index, style, items, handlers, selectedTripBookMenu, setSelectedTripBookMenu, t, user, isCompact }: RowComponentProps<{
    items: Tripbook[];
    user: any;
    t: any;
    isCompact: boolean;
    selectedTripBookMenu: string;
    setSelectedTripBookMenu: (id: string) => void;
    handlers: {
        onDownloadEbook: (code: string) => void;
        onShareEbook: (tripbook: Tripbook) => void;
        onEdit: (id: string) => void;
        onInfo: (tripbook: Tripbook) => void;
        onDelete: (id: string) => void;
        onAgencyClick?: (agencyId: string) => void;
    }
}>) => {
    if (!items) return <div/>
    const tripbook = items[index] as any;

    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const handleToggleMenu = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (selectedTripBookMenu === tripbook.id) {
            setSelectedTripBookMenu("");
        } else {
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            setCoords({ top: rect.bottom, left: rect.left - 180 });
            setSelectedTripBookMenu(tripbook.id);
        }
    };

    const closeMenu = () => setSelectedTripBookMenu("");

    // Derived values
    const travelerName = [tripbook.mainTraveler?.firstName, tripbook.mainTraveler?.lastName].filter(Boolean).join(' ')
    const travelerInitials = [tripbook.mainTraveler?.firstName?.[0], tripbook.mainTraveler?.lastName?.[0]].filter(Boolean).join('').toUpperCase()
    const hasDates = !!(tripbook.itinerary?.startDate && tripbook.itinerary?.endDate)
    const statusConfig = tripbook.ebookUrl
        ? { label: t('statusReady', {ns: 'dashboard', defaultValue: 'Listo'}), className: 'bg-green-100 text-green-700' }
        : tripbook.status === 'unused'
            ? { label: t('statusPending', {ns: 'dashboard', defaultValue: 'Pendiente'}), className: 'bg-neutralBlack-100 text-neutralBlack-500' }
            : { label: t('statusProcessing', {ns: 'dashboard', defaultValue: 'En proceso'}), className: 'bg-blue-50 text-blue-600' }

    // Bloques reutilizables — el ancho/posición cambia según isCompact, el contenido no
    const datesBlock = (
        <div className={isCompact ? "flex flex-col gap-1 min-w-[120px]" : "flex flex-col gap-1 w-[150px] shrink-0"}>
            {hasDates ? (
                <>
                    <div className="text-[13px] font-medium text-neutralBlack-700">
                        {getFormattedDateFromTimestamp(tripbook.itinerary.startDate, true)}
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-px bg-neutralBlack-300"/>
                        <div className="text-[13px] text-neutralBlack-500">
                            {getFormattedDateFromTimestamp(tripbook.itinerary.endDate, true)}
                        </div>
                    </div>
                </>
            ) : (
                <span className="text-[12px] text-neutralBlack-400 italic">{t('noDates', {ns: 'dashboard', defaultValue: 'Sin fechas'})}</span>
            )}
            {tripbook.partnerOrderID && (
                <div className="text-[12px] text-neutralBlack-500 mt-1">
                    Ref. <span className="font-medium text-neutralBlack-700">{tripbook.partnerOrderID}</span>
                </div>
            )}
        </div>
    )

    const codeBlock = (
        <div className={isCompact ? "flex flex-col gap-1 min-w-[110px]" : "flex flex-col gap-1 w-[130px] shrink-0"}>
            <span className="text-[10px] text-neutralBlack-400 uppercase tracking-wider font-medium">
                {t('bookingId', {ns: 'dashboard', defaultValue: 'Booking ID'})}
            </span>
            <span className="text-[12px] font-mono font-medium text-neutralBlack-700 tracking-wide">
                {tripbook.code ?? '—'}
            </span>
            <div className="flex flex-col gap-1">
                {tripbook.stripeProductCode != null && (
                    <span className="self-start text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        {t('paymentCode', {ns: 'dashboard', defaultValue: 'Payment'})}
                    </span>
                )}
                {tripbook.unlimitedRedemption === true && (
                    <span className="self-start text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                        {t('reusableCode', {ns: 'dashboard', defaultValue: 'Reusable'})}
                    </span>
                )}
            </div>
        </div>
    )

    const agencyBlock = user.role === 'tourOperator' ? (
        <div
            className={`flex ${isCompact ? 'flex-row items-center gap-2' : 'flex-col items-center justify-center gap-1.5 w-[100px] shrink-0'} ${handlers.onAgencyClick && tripbook.agencyData?.id ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`}
            onClick={(e) => {
                if (handlers.onAgencyClick && tripbook.agencyData?.id) {
                    e.stopPropagation();
                    handlers.onAgencyClick(tripbook.agencyData.id);
                }
            }}
        >
            {tripbook.agencyData?.logoName ? (
                <div className="w-[36px] h-[36px] bg-neutralBlack-100 rounded-full flex items-center justify-center p-1 shrink-0">
                    <img src={getAgencyAssetUrl(tripbook.agencyData.logoName)} alt={tripbook.agencyData.name} className="w-full h-full object-contain"/>
                </div>
            ) : (
                <div className="w-[36px] h-[36px] bg-neutralBlack-100 rounded-full flex items-center justify-center text-[11px] font-semibold text-neutralBlack-500 shrink-0">
                    {tripbook.agencyData?.name?.[0]?.toUpperCase() || '?'}
                </div>
            )}
            <div className={`text-[12px] text-neutralBlack-600 capitalize truncate ${isCompact ? 'text-left max-w-[110px]' : 'text-center w-full'}`}>
                {tripbook.agencyData?.name || '—'}
            </div>
        </div>
    ) : null

    return (
        <div style={style} className="w-full px-1 box-border">
            <div
                className="flex flex-row items-stretch gap-0 bg-white mb-4 rounded-xl overflow-hidden"
                style={{boxShadow: "0px 2px 8px 0px #0000000F"}}
            >
                {/* Cover miniature — franja izquierda */}
                <div className={isCompact ? "shrink-0 w-[90px]" : "shrink-0 w-[125px]"}>
                    <TripBookMiniature
                        widthPx={isCompact ? 90 : 125}
                        padding={false}
                        isRealistic={true}
                        trip={{
                            title: tripbook?.itinerary?.name || "Safari en Kenia",
                            coverImageURL: tripbook?.itinerary?.tripImageUrl || "https://storage.googleapis.com/tripsapp-prod-cdn/countryImageId/404.jpg?alt=media",
                            colorID: user.corporateColor,
                            startDate: tripbook?.itinerary?.startDate ? new Date(tripbook.itinerary.startDate) : new Date(),
                            agency: tripbook?.agencyData,
                        }}
                    />
                </div>

                {/* Main content */}
                <div className={isCompact ? "flex flex-col flex-1 gap-2 px-5 py-3 min-w-0 justify-center" : "flex flex-row flex-1 items-center gap-4 px-5 py-4 min-w-0"}>

                    {/* Col 1: Viajero */}
                    <div className={isCompact ? "flex flex-col gap-1 min-w-0 w-full" : "flex flex-col gap-1 min-w-0 w-[180px] shrink-0"}>
                        <div className="flex items-center gap-2">
                            {travelerInitials ? (
                                <div className="w-8 h-8 rounded-full bg-neutralBlack-200 flex items-center justify-center text-[11px] font-semibold text-neutralBlack-600 shrink-0">
                                    {travelerInitials}
                                </div>
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-neutralBlack-100 flex items-center justify-center shrink-0">
                                    <svg className="w-4 h-4 text-neutralBlack-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                    </svg>
                                </div>
                            )}
                            <div className="flex flex-col min-w-0">
                                <span className="text-[14px] font-semibold truncate leading-tight">
                                    {travelerName || <span className="text-neutralBlack-400 font-normal italic text-[13px]">{t('noTraveler', {ns: 'dashboard', defaultValue: 'Sin viajero'})}</span>}
                                </span>
                                {tripbook.travelerEmail && (
                                    <span className="text-[12px] text-neutralBlack-500 truncate">{tripbook.travelerEmail}</span>
                                )}
                            </div>
                        </div>
                        <span className={`self-start text-[11px] font-medium px-2 py-0.5 rounded-full mt-1 ${statusConfig.className}`}>
                            {statusConfig.label}
                        </span>
                    </div>

                    {!isCompact && <div className="w-px self-stretch bg-neutralBlack-100 shrink-0"/>}

                    {/* Col 2: Destino + circuito */}
                    <div className={isCompact ? "flex flex-col gap-1 w-full" : "flex flex-col gap-1 flex-1 min-w-0"}>
                        <div className="text-[14px] font-medium truncate capitalize">
                            {tripbook.itinerary?.name || <span className="text-neutralBlack-400 italic text-[13px]">{t('noCircuit', {ns: 'dashboard', defaultValue: 'Sin circuito'})}</span>}
                        </div>
                        {tripbook.itinerary?.countryCodes?.length > 0 && (
                            <div className="flex flex-row gap-1 flex-wrap">
                                {tripbook.itinerary.countryCodes.slice(0, 5).map((code: string) => (
                                    <img key={code} alt={code}
                                         src={getFlagUrl(code)}
                                         className="h-[13px] rounded-sm"
                                         onError={(e) => { e.currentTarget.style.display = 'none' }}/>
                                ))}
                            </div>
                        )}
                        {tripbook.observations && (
                            <span className="text-[12px] text-neutralBlack-500 truncate">{tripbook.observations}</span>
                        )}
                    </div>

                    {isCompact ? (
                        /* En pantallas estrechas, Fechas/Código/Agencia pasan a una fila de chips que hace wrap en vez de cortarse */
                        <div className="flex flex-row flex-wrap gap-x-5 gap-y-2 pt-2 mt-1 border-t border-neutralBlack-100">
                            {datesBlock}
                            {codeBlock}
                            {agencyBlock}
                        </div>
                    ) : (
                        <>
                            <div className="w-px self-stretch bg-neutralBlack-100 shrink-0"/>
                            {datesBlock}
                            <div className="w-px self-stretch bg-neutralBlack-100 shrink-0"/>
                            {codeBlock}
                            {agencyBlock && (
                                <>
                                    <div className="w-px self-stretch bg-neutralBlack-100 shrink-0"/>
                                    {agencyBlock}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Menú tres puntos */}
                <div className="flex items-center pr-3 shrink-0">
                    <div
                        className="size-[42px] rounded-full hover:bg-neutralBlack-100 flex items-center justify-center cursor-pointer"
                        onClick={handleToggleMenu}
                    >
                        <img width="23px" src={threeDotsMenuIcon} alt="menu"/>

                        {selectedTripBookMenu === tripbook.id && (
                            <MenuPortal top={coords.top} left={coords.left} onClose={closeMenu}>
                                {tripbook.ebookUrl && (
                                    <div className="flex flex-row gap-3 text-[16px] hover:bg-neutralBlack-100 px-4 py-3 cursor-pointer"
                                         onClick={(e) => { e.stopPropagation(); handlers.onDownloadEbook(tripbook.code); closeMenu(); }}>
                                        <div className="size-[20px]"><img src={downloadIcon} alt=""/></div>
                                        {t('downloadEbook', {ns: 'dashboard'})}
                                    </div>
                                )}
                                {user.role === 'tourOperator' && tripbook.ebookUrl && (
                                    <div className="flex flex-row gap-3 text-[16px] hover:bg-neutralBlack-100 px-4 py-3 cursor-pointer"
                                         onClick={(e) => { e.stopPropagation(); handlers.onShareEbook(tripbook); closeMenu(); }}>
                                        <div className="size-[20px]"><img src={IconShare} alt=""/></div>
                                        {t('shareEbook', {ns: 'dashboard'})}
                                    </div>
                                )}
                                {tripbook.status === "unused" && (
                                    <div className="flex flex-row gap-3 text-[16px] hover:bg-neutralBlack-100 px-4 py-3 cursor-pointer"
                                         onClick={(e) => { e.stopPropagation(); handlers.onEdit(tripbook.id); closeMenu(); }}>
                                        <div className="size-[20px]"><img src={editIcon} alt=""/></div>
                                        {t('editTripbook', {ns: 'dashboard'})}
                                    </div>
                                )}
                                <div className="flex flex-row gap-3 text-[16px] hover:bg-neutralBlack-100 px-4 py-3 cursor-pointer"
                                     onClick={(e) => { e.stopPropagation(); handlers.onInfo(tripbook); closeMenu(); }}>
                                    <div className="size-[20px]"><img src={infoIcon} alt=""/></div>
                                    {t('seeMoreInfo', {ns: 'dashboard'})}
                                </div>
                                {tripbook.status === "unused" && (
                                    <div className="flex flex-row gap-3 text-[16px] hover:bg-neutralBlack-100 px-4 py-3 cursor-pointer"
                                         onClick={(e) => { e.stopPropagation(); handlers.onDelete(tripbook.id); closeMenu(); }}>
                                        <div className="size-[20px]"><img src={deleteIcon} alt=""/></div>
                                        {t('delete', {ns: 'dashboard'})}
                                    </div>
                                )}
                            </MenuPortal>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const TripbooksContainer : React.FC<TripbooksContainerProps> = ({displayCreateTripItineraryDialog, setPath}) => {


    //translations
    const { t } = useTranslation('dashboard');


    const user = useSelector((state) => state.auth.user)
    const isAdmin = (user as any)?.role === 'admin'

    const [selectedAgencyId, setSelectedAgencyId] = useState<string | undefined>(undefined)
    const [debouncedKeyword, setDebouncedKeyword] = useState<string | undefined>(undefined)

    const { tripbooks: fetchedTripbooks, isLoadingTripbooks, refetchTripbooks } = useTripbooks(selectedAgencyId, user?.email, debouncedKeyword)
    const { agencies} = useAgencies()

    //States
    const [tripbooks, setTripbooks] = useState<Tripbook[]>([]);
    const [tripbookToEditDelete, setTripbookToEditDelete] = useState<Tripbook | null>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const [filteredTripbooks, setFilteredTripbooks] = useState<Tripbook[]>([]);
    const [sortBy, setSortBy] = useState<string>("date");
    const [selectedTripBookMenu, setSelectedTripBookMenu] = useState<string>("");
    const [tripbookToInspect, setTripbookToInspect] = useState<Tripbook | null>(null);
    const [shareEbookEmail, setShareEbookEmail] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [shareEbookConfirmationMessage, setShareEbookConfirmationMessage] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string>("all");

    const { ref: listContainerRef, width: listContainerWidth } = useContainerWidth();
    const isCompactLayout = listContainerWidth > 0 && listContainerWidth < WIDE_LAYOUT_BREAKPOINT;
    const rowHeight = isCompactLayout ? 264 : 100;

    const searchInputSubject = useMemo<Subject<string>>(() => {
        return new Subject<string>()
    }, [])
    const confirmDeleteTripBookDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const createTripBookDialog = useRef<React.ElementRef<typeof CreateUpdateTripBookDialog>>(null)
    const tripbookDetailDialog = useRef<React.ElementRef<typeof Dialog>>(null)
    const shareEbookDialog = useRef<React.ElementRef<typeof Dialog>>(null)


    const openCreateEditTripbookDialog = () => {
        trackAnalyticsEvent(AnalyticsEvents.CLICK_CREATE_TRIPBOOK)
        warmTripbookUseCase.execute().then(() => {

        }).catch(()=>{

        })
        createTripBookDialog!.current!.open()
    }

    useEffect(() => {
        if (fetchedTripbooks) {
            setTripbooks(fetchedTripbooks)
            setFilteredTripbooks(fetchedTripbooks)
        }
    }, [fetchedTripbooks])

    useEffect(() => {
        const subscription = searchInputSubject.pipe(
            debounceTime(700),
        ).subscribe((value) => {
            setDebouncedKeyword(value || undefined)
        })
        return () => subscription.unsubscribe()
    }, [searchInputSubject])

    useEffect(() => {
        // Función que manejará el evento click
        const handleClickOutside = (event) => {
            /*
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                // Si se hizo clic fuera del modal, ciérralo
                closeModal();
            }

             */
        }

        // Añadir el listener al montar el componente
        document.addEventListener('mousedown', handleClickOutside);

        // Limpiar el listener al desmontar el componente
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    },[])

    const closeModal = () => {
        // Lógica para ocultar el modal
        document.querySelectorAll('.menuModal').forEach((menu) => {
            menu.classList.add('hidden');
        });
    };


    const onChangeAgency = (e) => {
        setSelectedAgencyId(e.target.value === "all" ? undefined : e.target.value)
    }

    const onChangeSortBy = (e) => {
        setSortBy(e.target.value);
        setFilteredTripbooks(tripbooks.toSorted((a, b) => {
            if (e.target.value === "dateAsc") {
                return (a.itinerary?.startDate ?? 0) - (b.itinerary?.startDate ?? 0);
            } else if (e.target.value === "dateDesc") {
                return (b.itinerary?.startDate ?? 0) - (a.itinerary?.startDate ?? 0);
            } else {
                return (a.itinerary?.name ?? '').localeCompare(b.itinerary?.name ?? '');
            }
        }));
    }


    const deleteTripbook = () => {
        trackAnalyticsEvent(AnalyticsEvents.CLICK_DELETE_TRIPBOOK)

        deleteTripbookUseCase.execute(tripbookToEditDelete).then(() => {
            refetchTripbooks()
            confirmDeleteTripBookDialog!.current!.close()
        })

    }

    const downloadEbook = (invitationCode: string) => {
        //get api to /invitation-codes/{invitationCode}/ebook returning the pdf, http response is content-disposition
        // 	attachment; filename="Ebook-L8O167TO.pdf"
        downloadEbookUseCase.execute(invitationCode)
        .then((response) => {
            //content-disposition
            // 	attachment; filename="Ebook-L8O167TO.pdf"

            // Crear un enlace temporal para descargar el archivo
            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invitationCode}.pdf`); // Establecer el nombre del archivo
            document.body.appendChild(link);
            link.click();

        }).catch((error) => {
            console.log(error)
        }
        )
    }

    const handleTripbookSearchQuery = (value: string) => {
        setSearchInput(value)
        searchInputSubject.next(value)
    }

    // Muestra un tripbook concreto filtrando por su referencia en el buscador
    type CreatedTripbook = { partnerOrderID?: string, code?: string }
    const searchCreatedTripbook = (tripbook: CreatedTripbook | CreatedTripbook[]) => {
        const created = Array.isArray(tripbook) ? tripbook[0] : tripbook
        const keyword = created?.partnerOrderID ?? created?.code
        if (!keyword) {
            refetchTripbooks()
            return
        }
        setSearchInput(keyword)
        searchInputSubject.next(keyword)
        // aplicamos el filtro al instante, sin esperar al debounce
        setDebouncedKeyword(keyword)
        // limpiamos el resto de filtros para que el tripbook creado sea visible
        setStatusFilter("all")
        setSelectedAgencyId(undefined)
    }

    const shareEbook = () => {
        trackAnalyticsEvent(AnalyticsEvents.CLICK_SHARE_TRIPBOOK)
        setIsLoading(true)
        shareTripbookUseCase.execute(tripbookToInspect.id, shareEbookEmail).then(() => {
            setShareEbookConfirmationMessage(t('ebookShared', {ns: 'dashboard'}))
            setTimeout(() => {
                shareEbookDialog!.current!.close()
                setShareEbookConfirmationMessage(undefined)
            }, 2500)
        }).catch(() => {
            setShareEbookConfirmationMessage(t('ebookSharedError', {ns: 'dashboard'}))
            setTimeout(() => {
                shareEbookDialog!.current!.close()
                setShareEbookConfirmationMessage(undefined)
            }, 4500)
        }).finally(() => {
            setShareEbookEmail("")
            setIsLoading(false)

        })
    }

    const isSharedEbookEmailValid = ()=> {
        return RegExp(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/).test(shareEbookEmail)
    }

    const displayTripbooks = useMemo(() => {
        if (statusFilter === "all") return filteredTripbooks;
        return filteredTripbooks.filter((tb: any) => {
            if (statusFilter === "ready") return !!tb.ebookUrl;
            if (statusFilter === "pending") return tb.status === "unused" && !tb.ebookUrl;
            if (statusFilter === "processing") return tb.status !== "unused" && !tb.ebookUrl;
            return true;
        });
    }, [filteredTripbooks, statusFilter]);

    const itemData = useMemo(() => ({
        items: displayTripbooks,
        user: user,
        t: t,
        isCompact: isCompactLayout,
        selectedTripBookMenu: selectedTripBookMenu,
        setSelectedTripBookMenu: setSelectedTripBookMenu,
        handlers: {
            onDownloadEbook: (code: string) => downloadEbook(code),
            onShareEbook: (tripbook: Tripbook) => {
                setTripbookToInspect(tripbook);
                shareEbookDialog!.current!.open();
            },
            onEdit: (id: string) => {
                trackAnalyticsEvent(AnalyticsEvents.CLICK_EDIT_TRIPBOOK);
                setTripbookToEditDelete(id);
                openCreateEditTripbookDialog();
            },
            onInfo: (tripbook: Tripbook) => {
                setTripbookToInspect(tripbook);
                trackAnalyticsEvent(AnalyticsEvents.CLICK_SEE_MORE_INFO_TRIPBOOK);
                tripbookDetailDialog!.current!.open();
            },
            onDelete: (id: string) => {
                setTripbookToEditDelete(id);
                confirmDeleteTripBookDialog!.current!.open();
            },
            onAgencyClick: (setPath && isAdmin) ? (agencyId: string) => setPath('agency/' + agencyId) : undefined,
        }
    }), [displayTripbooks, user, isCompactLayout, selectedTripBookMenu, setPath]);




    return <div className="bg-white h-full rounded-2xl px-7 pt-6 pb-5 flex flex-col w-full">
        <div className="flex flex-row justify-between items-center w-full gap-6">
            <div className="text-[26px] font-semibold tracking-tight shrink-0">{t('trips')}</div>
            <div className="h-[40px] max-w-[420px] w-full flex items-center">
                <AutocompleteSearch
                    options={[]}
                    placeholder={t('Search', { ns: 'dashboard' })}
                    searchQuery={searchInput}
                    className="w-full"
                    onSearchQueryChange={handleTripbookSearchQuery}
                    onSelectOption={() => {}}
                />
            </div>
            <Button variant={"primary"} className="text-[15px] min-h-[40px] max-h-[40px] px-5 shrink-0" onClick={() => {
                setTripbookToEditDelete(null)
                displayCreateTripItineraryDialog(false)
                openCreateEditTripbookDialog()
            }}>
                {t('addTrip', {ns:'dashboard'})}
            </Button>
        </div>
        <div id="filters" className="flex flex-row gap-3 mt-5 flex-wrap items-center">
            { user.role === 'tourOperator' && (
                <select
                    className="text-[13px] bg-neutralBlack-50 px-3 py-2 border border-neutralBlack-200 rounded-lg text-neutralBlack-700 cursor-pointer hover:border-neutralBlack-400 transition-colors outline-none"
                    value={selectedAgencyId ?? "all"}
                    onChange={onChangeAgency}
                >
                    <option value="all">{t('agencies', {ns:'dashboard'})}</option>
                    {(Array.isArray(agencies) ? agencies : []).sort(
                        (a, b) => a.name.localeCompare(b.name)
                    ).map((agency) => (
                        <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                </select>
            )}
            <select
                className="text-[13px] bg-neutralBlack-50 px-3 py-2 border border-neutralBlack-200 rounded-lg text-neutralBlack-700 cursor-pointer hover:border-neutralBlack-400 transition-colors outline-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
            >
                <option value="all">{t('statusAll', {ns: 'dashboard', defaultValue: 'All statuses'})}</option>
                <option value="ready">{t('statusReady', {ns: 'dashboard', defaultValue: 'Ready'})}</option>
                <option value="pending">{t('statusPending', {ns: 'dashboard', defaultValue: 'Pending'})}</option>
                <option value="processing">{t('statusProcessing', {ns: 'dashboard', defaultValue: 'Processing'})}</option>
            </select>
            <select
                className="text-[13px] bg-neutralBlack-50 px-3 py-2 border border-neutralBlack-200 rounded-lg text-neutralBlack-700 cursor-pointer hover:border-neutralBlack-400 transition-colors outline-none"
                onChange={onChangeSortBy}
            >
                <option value="name">{t('itinerary', {ns: 'dashboard'})}</option>
                <option value="dateAsc">{t('sortDateAsc', {ns: 'dashboard', defaultValue: 'Date (oldest first)'})}</option>
                <option value="dateDesc">{t('sortDateDesc', {ns: 'dashboard', defaultValue: 'Date (newest first)'})}</option>
            </select>
        </div>
        <div ref={listContainerRef} className="mt-[24px] w-full flex-1 h-full min-h-0 relative">
            {isLoadingTripbooks ? (
                /* ESTADO DE CARGA */
                <div className="w-full h-full flex items-center justify-center">
                    <IconSpinner className="w-12 h-12 animate-spin text-neutralBlack-300" />
                </div>
            ) : (
                /* LISTA DE RESULTADOS */
                itemData.items.length > 0 ? (
                    <List
                        rowHeight={rowHeight}
                        rowCount={itemData.items.length}
                        rowProps={itemData}
                        rowComponent={TripbookRow}
                        // Nota: react-window suele requerir height y width explícitos.
                        // Si tu componente List ya lo maneja internamente, déjalo así.
                        // Si no, necesitarás AutoSizer.
                    />
                ) : (
                    /* ESTADO VACÍO (Opcional pero recomendado) */
                    <div className="w-full h-full flex items-center justify-center text-neutralBlack-500 ">
                        {t('noResults', { ns: 'dashboard', defaultValue: "No se encontraron tripbooks" })}
                    </div>
                )
            )}
        </div>
        <ConfirmDialog
            ref={confirmDeleteTripBookDialog}
            title={t('deleteTripbook', { ns: 'dashboard' })}
            confirmLabel={t('delete', { ns: 'dashboard' })}
            confirmIcon={IconTrash}

            cancelLabel={t('cancel', { ns: 'dashboard' })}
            onConfirm={()=>{
                deleteTripbook(selectedTripBookMenu)
            }}
        />
        <CreateUpdateTripBookDialog
            ref={createTripBookDialog}
            title={t('addTrip', { ns: 'dashboard' })}
            confirmLabel={t('create', { ns: 'dashboard' })}
            confirmIcon={IconTrash}
            onComplete={(tripbook) => {
                createTripBookDialog!.current!.close()
                searchCreatedTripbook(tripbook)
            }}
            cancelLabel={t('cancel', { ns: 'dashboard' })}
            tripbook={tripbookToEditDelete ? tripbooks.find((tripbook) => tripbook.id === tripbookToEditDelete) : {
                id: "",
                name: "",
                startDate: '',
                endDate: '',
                agency: undefined,
                partnerOrderID: "",
                observations: "",
                itinerary: undefined
            }}

            openCreateTripItineraryDialog={displayCreateTripItineraryDialog}
        />
        <Dialog ref={shareEbookDialog} title={t('shareEbookAgency', {ns: 'dashboard'})} withHeaderDelimiter={true} withCloseButton width={"mid-wide"}>
            {!shareEbookConfirmationMessage ?
            <div className={"flex flex-col gap-4"}>
                <div>{t('contactEmail', {ns: 'dashboard'})}</div>
                <Input label={t('email', {ns: 'dashboard'})} placeholder={t('email', {ns: 'dashboard'})} value={shareEbookEmail} onChange={(e)=> {
                    setShareEbookEmail(e.target.value)
                }} />
                <Button className={"max-w-[150px]"} disabled={!isSharedEbookEmailValid()} variant={"primary"} onClick={shareEbook}>{t('send', {ns: 'dashboard'})}</Button>
            </div> : <div className="text-[18px] ">{shareEbookConfirmationMessage}</div>}
            {isLoading && (
                <div className="absolute inset-0 bg-neutralBlack-100 opacity-75 flex rounded-xl">
                    <IconSpinner className="size-16 m-auto" />
                </div>
            )}
        </Dialog>
        <Dialog ref={tripbookDetailDialog} title={"Ref. "+tripbookToInspect?.partnerOrderID} withHeaderDelimiter={true} withCloseButton width={"wide"}>
        <div className="flex flex-col">
            <div className="flex flex-row">
                <div className="scale-75">
                <TripBookMiniature size={"mid"}
                                   isRealistic={true}
                                   trip={{
                                       title: tripbookToInspect?.itinerary?.name || "Safari en Kenia",
                                       coverImageURL: tripbookToInspect?.itinerary?.tripImageUrl || "https://storage.googleapis.com/tripsapp-prod-cdn/countryImageId/404.jpg?alt=media",
                                       colorID: user.corporateColor,
                                       startDate: tripbookToInspect?.itinerary?.startDate ? new Date(tripbookToInspect.itinerary.startDate) : new Date(),
                                       agency: tripbookToInspect?.agencyData,
                                   }}
                                   />
                </div>
                <div className="flex flex-col gap-3 mt-6">
                    <div className=" text-[22px]">
                        {tripbookToInspect?.itinerary?.name}
                    </div>
                    <div className=" text-[18px] text-neutralBlack-500">
                        {getFormattedDateFromTimestamp(tripbookToInspect?.itinerary?.startDate, true)} - {getFormattedDateFromTimestamp(tripbookToInspect?.itinerary?.endDate, true)}
                    </div>
                    <div
                        className={`text-[22px] flex flex-row gap-2 ${setPath && isAdmin && tripbookToInspect?.agencyData?.id ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`}
                        onClick={() => {
                            if (setPath && isAdmin && tripbookToInspect?.agencyData?.id) {
                                setPath('agency/' + tripbookToInspect.agencyData.id)
                            }
                        }}
                    >
                        {tripbookToInspect?.agencyData?.logoName ? <div className="w-[50px] h-[50px] bg-white rounded-full p-1 flex items-center"><img src={getAgencyAssetUrl(tripbookToInspect?.agencyData?.logoName)}/></div> : null}
                        <div className="text-[14px] lowercase flex items-center capitalize">{tripbookToInspect?.agencyData?.name}</div>
                    </div>
                </div>
            </div>
            { tripbookToInspect?.observations ?
            <div className="flex flex-col ml-10">
                <div className=" text-[18px]">{t('observations', {ns: "dashboard"})}</div>
                <div className=" text-[18px] text-neutralBlack-500">{tripbookToInspect?.observations}</div>
            </div> : null }
        </div>
        </Dialog>
        {isLoading && (
            <div className="absolute inset-0 bg-neutralBlack-100 opacity-75 flex rounded-xl">
                <IconSpinner className="size-16 m-auto" />
            </div>
        )}
    </div>;
}
